import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useMsal } from "@azure/msal-react";
import { loginRequest } from "./authConfig";
import { setColumnMap, setUserMap, setPhotoMap, setPeopleDirectory, gf, DISPLAY_NAMES } from "./fieldConfig";
import { applyCustomToUserMap, getCustomStore, saveCustomPerson } from "./peopleDb";

const DataContext = createContext(null);
export function useData() { return useContext(DataContext); }
export function ReportDataScope({ items, children }) {
  const parent = useData();
  return <DataContext.Provider value={{ ...parent, items }}>{children}</DataContext.Provider>;
}

// ─── Cache ถาวรของผล Sync 365 ───────────────────────────────────
// ไม่มี TTL — อยู่จนกว่าจะกด "Sync 365" ใหม่เองเท่านั้น
// แยก key rawMap กับ photoMap คนละก้อน — ถ้า photoMap เกินโควต้า (รูป base64 เยอะ)
// rawMap (dept/jobTitle/name) ยังเซฟติดได้ตามปกติ ไม่พังพร้อมกันทั้งคู่
const RAW_CACHE_KEY   = "dtc_sp_rawmap_v1";
const PHOTO_CACHE_KEY = "dtc_sp_photomap_v1";

function loadJson(key) {
  try { return JSON.parse(localStorage.getItem(key) ?? "null"); }
  catch { return null; }
}
// คืน true ถ้าเซฟสำเร็จ, false ถ้าพัง (เช่น เกินโควต้า)
function saveJson(key, data) {
  try { localStorage.setItem(key, JSON.stringify(data)); return true; }
  catch (e) { console.warn(`บันทึก ${key} ไม่สำเร็จ:`, e.message); return false; }
}

async function fetchPeopleDirectoryFromServer(token) {
  const res = await fetch("/api/people-list", {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  const data = await res.json().catch(() => []);
  if (!res.ok) throw new Error(data?.error || `People API HTTP ${res.status}`);
  return Array.isArray(data) ? data : [];
}

async function fetchLiveReportItemsFromServer(token) {
  const res = await fetch("/api/live-report-items", {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error || `Live Report API HTTP ${res.status}`);
  return data;
}

function buildPeopleIndexes(people) {
  const byEmail = new Map();
  const byNameApi = new Map();
  const byName = new Map();
  people.forEach(person => {
    const email = String(person?.email || "").trim().toLowerCase();
    const nameApi = String(person?.nameApi || "").trim().toLowerCase();
    const name = String(person?.name || "").trim().toLowerCase();
    if (email) byEmail.set(email, person);
    if (nameApi) byNameApi.set(nameApi, person);
    if (name) byName.set(name, person);
  });
  return { byEmail, byNameApi, byName };
}

function mergePeopleDirectoryIntoUserMap(userMap, people) {
  const { byEmail, byNameApi, byName } = buildPeopleIndexes(people);
  const merged = {};
  Object.entries(userMap || {}).forEach(([id, user]) => {
    const email = String(user?.email || "").trim().toLowerCase();
    const name = String(user?.name || "").trim().toLowerCase();
    const person = (email && byEmail.get(email)) || (name && byNameApi.get(name)) || (name && byName.get(name));
    merged[id] = person ? {
      ...user,
      email: person.email || user.email || "",
      name: person.name || user.name || "",
      nameApi: person.nameApi || user.name || "",
      dept: person.dept || user.dept || "",
      jobTitle: person.jobTitle || user.jobTitle || "",
      division: person.division || user.division || "",
      nickname: person.nickname || user.nickname || "",
      lineAccount: person.lineAccount || user.lineAccount || "",
      employeeCode: person.employeeCode || user.employeeCode || "",
      photoUrl: person.photoUrl || user.photoUrl || null,
    } : user;
  });
  return merged;
}

function photoMapFromPeople(userMap) {
  const map = {};
  Object.entries(userMap || {}).forEach(([id, user]) => {
    if (user?.photoUrl) map[id] = user.photoUrl;
  });
  return map;
}

function collectLookupIds(items) {
  const ids = new Set();
  (items || []).forEach(item => {
    Object.entries(item || {}).forEach(([key, value]) => {
      if (!key.endsWith("LookupId")) return;
      if (value === null || value === undefined || value === "") return;
      ids.add(String(value));
    });
  });
  return ids;
}

function missingLookupIds(items, userMap) {
  return Array.from(collectLookupIds(items))
    .filter(id => !String(userMap?.[id]?.name || "").trim());
}

function pickUsersMissingPhotos(items, userMap, photoMap) {
  const result = {};
  collectLookupIds(items).forEach(id => {
    const user = userMap?.[id];
    if (!user?.email || photoMap?.[id]) return;
    result[id] = user;
  });
  return result;
}

export function DataProvider({ children }) {
  const { instance, accounts } = useMsal();
  // ใช้ accounts.length เช็คแทน useIsAuthenticated() — useIsAuthenticated()
  // subscribe MSAL event ของตัวเอง (re-render ทุกครั้งที่ MSAL ยิง event ใดๆ
  // เช่น silent token renewal เบื้องหลัง) ยิ่งมี hook ลักษณะนี้ซ้อนกันหลายตัว
  // ในแอป ยิ่งเพิ่มโอกาสชนกับ render หนักๆ (เช่นหน้าตารางงาน) จนเกิด React
  // error #300 — accounts มาจาก useMsal() ตัวเดียวที่มีอยู่แล้วพอ ไม่ต้อง
  // subscribe เพิ่มอีกชั้น
  const [allItems, setAllItems]   = useState([]);
  const [columnMap, setColMap]    = useState({});
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState(null);
  const [periodMode, setPeriodMode] = useState("all"); // all | month | year
  const [monthFilter, setMonthFilter] = useState("all");
  const [yearFilter, setYearFilter] = useState("all");
  const [dataVersion, setDataVersion] = useState(0);
  const [userMapState,    setUserMapState]    = useState({}); // merged (API + custom) → ใช้ใน chart/display
  const [rawUserMapState, setRawUserMapState] = useState({}); // raw จาก API เท่านั้น → ใช้ใน People admin
  const [photoMapState, setPhotoMapState] = useState({});
  const [userFetchError, setUserFetchError] = useState(null);
  const [allSiteLists, setAllSiteLists] = useState([]);
  const [syncing365, setSyncing365] = useState(false);

  // เก็บไว้ใช้ตอนกด "Sync 365" ทีหลัง (ไม่ต้องเรียก fetchAllData ใหม่)
  const siteIdRef   = useRef(null);
  const userInfoRef = useRef({});
  // กัน loadData ถูกเรียกซ้อนกัน (เช่น ห้องเลขา sync หลายงานติดกัน หรือผู้ใช้
  // เปลี่ยนหน้าขณะ refresh เดิมยังไม่จบ) — ยิ่งเรียก acquireTokenSilent ถี่
  // ยิ่งเพิ่มโอกาสชนกับ MSAL event callback แล้วเกิด React error #300
  const loadingRef = useRef(false);
  // cache access token เอง (อายุ ~60-90 นาที) แทนเรียก instance.acquireTokenSilent
  // ใหม่ทุกครั้งที่ refresh() — แต่ละครั้งที่เรียก MSAL จะยิง event ภายในของมันเอง
  // (synchronous บางจุด แก้ฝั่งเราไม่ได้) ซึ่งถ้าไปชนตอนหน้าตารางงาน (ตารางใหญ่
  // ที่สุดในแอป ใช้เวลา render นานกว่าหน้าอื่น) กำลัง render จะเกิด React error #300
  // ลดความถี่ที่เรียก MSAL ลง ลดโอกาสชนได้มาก โดยเฉพาะตอนห้องเลขา sync ถี่ๆ
  const tokenCacheRef = useRef({ token: null, expiresOn: 0 });

  const getAccessToken = useCallback(async () => {
    const now = Date.now();
    if (tokenCacheRef.current.token && tokenCacheRef.current.expiresOn - 120000 > now) {
      return tokenCacheRef.current.token;
    }
    const result = await instance.acquireTokenSilent({
      ...loginRequest,
      account: accounts[0],
    }).catch(() => instance.acquireTokenPopup(loginRequest));
    tokenCacheRef.current = {
      token: result.accessToken,
      expiresOn: result.expiresOn ? new Date(result.expiresOn).getTime() : now + 50 * 60000,
    };
    return result.accessToken;
  }, [instance, accounts]);

  const getApiToken = useCallback(async () => {
    const result = await instance.acquireTokenSilent({
      ...loginRequest,
      account: accounts[0],
    }).catch(() => instance.acquireTokenPopup(loginRequest));
    return result.idToken;
  }, [instance, accounts]);

  const loadData = useCallback(async () => {
    if (!accounts.length || loadingRef.current) return;
    loadingRef.current = true;
    setLoading(true);
    setError(null);
    try {
      const apiToken = await getApiToken();
      const data = await fetchLiveReportItemsFromServer(apiToken);
      const items = Array.isArray(data?.items) ? data.items : [];
      const peopleDirectory = Array.isArray(data?.people) ? data.people : [];
      const userMap = data?.userMap && typeof data.userMap === "object" ? data.userMap : {};
      const photoMap = data?.photoMap && typeof data.photoMap === "object" ? data.photoMap : {};

      setColumnMap({});
      setPeopleDirectory(peopleDirectory);
      setColMap({});
      setAllItems(items);
      siteIdRef.current = null;
      userInfoRef.current = {};
      setUserFetchError(null);
      setAllSiteLists([]);
      setRawUserMapState({});
      setUserMap(userMap);
      setUserMapState(userMap);
      setPhotoMap(photoMap);
      setPhotoMapState(photoMap);
      setDataVersion(v => v + 1);
    } catch (err) {
      console.error(err);
      setError(err.message);
    }
    setLoading(false);
    loadingRef.current = false;
  }, [accounts, getApiToken]);

  // M365 enrichment runs on the server; work stays in the local database.
  const syncMicrosoft365 = useCallback(async () => {
    if (!accounts.length) return;
    setSyncing365(true);
    setError(null);
    try {
      const token = await getApiToken();
      const response = await fetch("/api/sync-people", { headers: { Authorization: "Bearer " + token } });
      if (!response.ok) throw new Error("อัปเดตข้อมูล Microsoft 365 ไม่สำเร็จ");
      await loadData();
    } catch (err) { setError(err.message); }
    finally { setSyncing365(false); }
  }, [accounts, getApiToken, loadData]);

  useEffect(() => {
    if (accounts.length > 0) loadData();
  }, [accounts, loadData]);

  // กรองเดือนใช้ "วันที่แจ้ง" (requestDate) เป็นหลัก — fallback เป็น Created
  // เฉพาะรายการเก่าที่ยังไม่มีค่า วันที่แจ้ง
  const availableMonths = useMemo(() => {
    const seen = new Set();
    allItems.forEach(item => {
      const d = gf(item, "requestDate") || item["Created"];
      if (d) {
        const dt = new Date(d);
        if (!isNaN(dt)) seen.add(`${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,"0")}`);
      }
    });
    return Array.from(seen).sort().reverse();
  }, [allItems]);

  const availableYears = useMemo(() => {
    const seen = new Set();
    allItems.forEach(item => {
      const d = gf(item, "requestDate") || item["Created"];
      if (d) {
        const dt = new Date(d);
        if (!isNaN(dt)) seen.add(String(dt.getFullYear()));
      }
    });
    return Array.from(seen).sort().reverse();
  }, [allItems]);

  const items = useMemo(() => {
    if (periodMode === "all") return allItems;
    return allItems.filter(item => {
      const d = new Date(gf(item, "requestDate") || item["Created"] || "");
      if (isNaN(d)) return false;
      if (periodMode === "year") {
        if (yearFilter === "all") return true;
        return String(d.getFullYear()) === yearFilter;
      }
      if (monthFilter === "all") return true;
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}` === monthFilter;
    });
  }, [allItems, periodMode, monthFilter, yearFilter]);

  return (
    <DataContext.Provider value={{
      items, allItems, columnMap,
      loading, error,
      periodMode, setPeriodMode,
      monthFilter, setMonthFilter,
      yearFilter, setYearFilter,
      availableMonths, availableYears,
      refresh: loadData,
      syncMicrosoft365, syncing365,
      dataVersion,
      userMapState,    // merged: API + custom (ใช้ใน chart/display)
      rawUserMapState, // raw: API เท่านั้น (ใช้ใน People admin → คอลัมน์ API)
      photoMapState, userFetchError, allSiteLists,
    }}>
      {children}
    </DataContext.Provider>
  );
}
